'use client'

import { useEffect } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { getStoredConsent, hasAnalyticsConsent, hasMarketingConsent } from './CookieConsentBanner'
import { gpcFromNavigator } from '@/lib/identity/consent'
import { IS_NON_PRODUCTION_BUILD } from '@/lib/analytics/non-production-build'
import { readVisitorIntentMemory } from '@/lib/analytics/visitor-intent'
import {
  CLARITY_PROJECT_ID,
  clarityConsent,
  decideClarity,
  haltClarity,
  startClarity,
  withdrawClarity,
  CLARITY_ANALYTICS_OFF_EVENT,
  type ClarityPageContext,
  type ClarityWindow,
} from '@/lib/analytics/clarity'

/**
 * Microsoft Clarity session recordings and heatmaps (Matt 2026-10-14). The rule,
 * the consent handling and the values sent live in lib/analytics/clarity.ts; this
 * component only reads the browser and applies the decision.
 *
 * Mounted once, beside PageViewTracker in AnalyticsScripts (not on /admin). It
 * decides again on every address change, on every banner answer
 * (`cookie-consent`), and when it unmounts. Once Clarity runs, the runtime also
 * judges every history navigation BEFORE the address changes (mayRecord), so a
 * client-side walk into /account or /admin stops Clarity before clarity-js can
 * restart itself on that page. Off while CLARITY_PROJECT_ID is null: nothing is
 * read and nothing loads.
 *
 * useSearchParams is read so a change to the query re-decides: a `?_pid=`
 * arrival is not recorded until PersonIdentityBridge strips the person token
 * from the address bar (Clarity sends the full address).
 */
export default function ClarityRecorder() {
  const pathname = usePathname()
  const query = useSearchParams()?.toString() ?? ''

  useEffect(() => {
    syncClarity()
  }, [pathname, query])

  useEffect(() => {
    const onConsent = () => syncClarity()
    // The analytics switch in the banner's settings went off. Stop at once,
    // before the choice is saved: Clarity is not allowed to upload what the
    // visitor does on the way to the Save button (Measurement test 4).
    const onAnalyticsOff = () => {
      try {
        withdrawClarity(document, window.location.hostname)
      } catch {
        /* recording never breaks a page */
      }
    }
    window.addEventListener('cookie-consent', onConsent)
    window.addEventListener(CLARITY_ANALYTICS_OFF_EVENT, onAnalyticsOff)
    return () => {
      window.removeEventListener('cookie-consent', onConsent)
      window.removeEventListener(CLARITY_ANALYTICS_OFF_EVENT, onAnalyticsOff)
      haltClarity()
    }
  }, [])

  return null
}

function pageContext(): ClarityPageContext {
  const memory = readVisitorIntentMemory()
  return {
    pathname: window.location.pathname,
    visitorIntent: memory.intent,
    primaryMarket: memory.primaryMarket,
  }
}

/**
 * The decision for an address, from what the browser holds now (G3, Counsel
 * 2026-10-22). hasAnalyticsConsent is the rule GA4 uses too: a stored answer
 * wins; with no answer analytics is on in the US (rr_cr=0) and off in the
 * EEA, UK, Switzerland and anywhere the region is unknown. Global Privacy
 * Control keeps it off unless the visitor turned analytics on in Your privacy
 * choices after the signal (gpcAnalytics). `stored` tells a withdrawal apart
 * from no answer.
 */
function decide(url: Pick<URL, 'pathname' | 'hostname' | 'search'>) {
  const gpcSignal = gpcFromNavigator(typeof navigator !== 'undefined' ? navigator : undefined)
  const stored = getStoredConsent()
  const consented = hasAnalyticsConsent()
  // A GPC visitor who opted back in to analytics is not blocked by the signal.
  const gpc = gpcSignal && !consented
  const decision = decideClarity({
    projectId: CLARITY_PROJECT_ID,
    nonProductionBuild: IS_NON_PRODUCTION_BUILD,
    pathname: url.pathname,
    host: url.hostname,
    search: url.search,
    userAgent: navigator.userAgent,
    webdriver: navigator.webdriver,
    cookieHeader: document.cookie,
    gpc,
    analyticsConsent: consented,
  })
  return { decision, consented, gpc, answered: stored !== null }
}

/** May Clarity follow a navigation to this address? Read before the address changes. */
function mayRecord(url: URL): boolean {
  return decide(url).decision.load
}

/** Read the browser, decide, then load, keep, halt or withdraw. Never throws. */
export function syncClarity(): void {
  // Off until Matt sends the project id: nothing is read, written or loaded.
  if (!CLARITY_PROJECT_ID) return
  try {
    const { decision, consented, gpc, answered } = decide(window.location)
    if (decision.load) {
      startClarity(window as unknown as ClarityWindow, {
        projectId: CLARITY_PROJECT_ID,
        consent: clarityConsent({ analytics: true, marketing: hasMarketingConsent() }),
        pageContext,
        mayRecord,
      })
      return
    }
    // A stored answer with analytics off, or GPC, is a withdrawal whatever else
    // blocks the page, so the cookies go even when the visitor answers the banner
    // on /account. No answer at all calls nothing: Clarity was never loaded, and
    // no tag is ever injected just to send a denial.
    if (gpc || (answered && !consented)) {
      withdrawClarity(document, window.location.hostname)
      return
    }
    if (!consented) return
    haltClarity()
  } catch {
    /* recording never breaks a page */
  }
}
